import React, { useState, useEffect } from 'react';
import { X, Clock, Gift } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ExitIntentPopup = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    // Don't show for logged-in users
    if (user) {
      return;
    }

    // Check if popup was already shown in this session
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) {
      return;
    }

    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves through the top of the page
      // And double-check user is still not logged in
      const isLoggedIn = localStorage.getItem('token');
      if (e.clientY <= 0 && !hasShown && !isLoggedIn) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitPopupShown', 'true');
      }
    };

    // Add delay before activating exit intent
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // Wait 5 seconds before enabling

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [hasShown, user]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleClaimDiscount = () => {
    setIsVisible(false);
    navigate('/register?discount=true');
  };

  // Don't render if user is logged in or popup shouldn't be visible
  if (!isVisible || user) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-1 rounded-full hover:bg-slate-100 transition-colors z-10"
          data-testid="exit-popup-close"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Gift className="w-8 h-8" />
            <span className="text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
              First-Time Offer
            </span>
          </div>
          <h2 className="text-2xl font-bold">Wait! Don't miss out!</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-3xl font-bold text-slate-800">R22.50</span>
              <span className="text-lg text-slate-400 line-through">R25.00</span>
            </div>
            <p className="text-amber-600 font-semibold">Save 10% on your first month!</p>
          </div>

          <div className="bg-slate-50 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-2 text-slate-600 mb-3">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium">Offer valid for 24 hours</span>
            </div>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Create 1 professional CV for FREE
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Access AI-powered tools
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                Upgrade anytime for clean PDFs
              </li>
            </ul>
          </div>

          <button
            onClick={handleClaimDiscount}
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            data-testid="exit-popup-claim-btn"
          >
            Claim My 10% Discount
          </button>

          <p className="text-center text-xs text-slate-400 mt-4">
            Create a free account to lock in your discount. No payment required now.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExitIntentPopup;
